import { Story } from "@/components/builder/stories/context";
import { buildStoryUrl, extractStories } from "@/components/builder/stories/list/helper";
import StoriesGenreFilter from "@/components/builder/stories/list/stories-genre-filter";
import Spinner from "@/components/spinner";
import { PLACEHOLDER_IMAGE } from "@/util/constants";
import { zeroPrefixedNumber } from "@/util/zero-prefix-number";
import { useSearchParam } from "@/hooks/use-search-param";
import { useTranslations } from "@/libs/base/components/locale-provider";
import TranslatedLink from "@/libs/base/components/translated-link";
import { cn } from "@/libs/base/utils/layout";
import { Builder } from "@builder.io/react";
import { useEffect } from "react";
import { useInView } from "react-intersection-observer";
import { useStoriesListContext } from "./context";

interface ComponentProps {
  initialOffset?: number;
  enableInfiniteScroll?: boolean;
  maxTagsToShow: number;
}

export interface StoriesTranslations {
  noMatchingStories: string;
}

export default function StoriesList(props: ComponentProps) {
  const {
    builder: { stories: translations },
  } = useTranslations();

  const [search, setSearch] = useSearchParam("search", "");

  const ctx = useStoriesListContext();
  const { ref: infiniteScrollRef, inView } = useInView();

  const initialOffset = props.initialOffset || 0;
  const stories = extractStories(Array.from(ctx.stories), initialOffset).filter(
    (story) =>
      story.data.title?.toLowerCase().includes(search.toLowerCase()) ||
      story.data.description?.toLowerCase().includes(search.toLowerCase()),
  );

  useEffect(() => {
    if (inView && !ctx.loadingStories) {
      ctx.loadMoreStories();
    }
  }, [inView]);

  if (stories.length === 0 && Builder.isEditing) {
    return (
      <p className="text-center text-lg leading-8 text-gray-600">No stories found, but message is shown in editor</p>
    );
  }

  return (
    <div className={`grid w-full grid-cols-1 gap-5 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5`}>
      <div className="flex w-full flex-col gap-4 sm:mt-0 md:col-span-2">
        <input
          type="text"
          className="w-full rounded-full border border-lightgray-300 px-5 transition-colors placeholder:text-lightgray-300 focus-within:border-black focus:ring-0"
          aria-label={translations.search}
          placeholder={translations.search}
          value={search}
          onChange={(e) => setSearch((e.target as HTMLInputElement).value)}
        />

        <div className="hidden sm:block">
          <SearchResults search={search} results={stories.length} />
        </div>
      </div>

      <div className="lg:col-start-4 xl:col-end-6">
        <StoriesGenreFilter />
      </div>

      <div className="col-span-full sm:hidden">
        <SearchResults search={search} results={stories.length} />
      </div>

      {stories.length > 0
        ? stories.map((story) => (
            <StoryElement
              key={story.id}
              story={story}
              selectGenre={(genre) => ctx.selectGenres([genre])}
              maxTagsToShow={props.maxTagsToShow}
            />
          ))
        : !ctx.loadingStories && (
            <div className="col-span-full mx-auto my-16 flex flex-col items-center gap-9 sm:w-fit sm:max-w-max sm:gap-17">
              <p className="flex w-full flex-col rounded-full border border-lightgray-100 bg-lightgray-200 px-9 py-4 text-center text-lg text-black sm:px-16 sm:py-5 sm:text-xl">
                {translations.no_matching_stories.message}
              </p>

              <div className="flex justify-between gap-4 font-light sm:gap-8 sm:px-9 sm:text-sm">
                <p>{translations.no_matching_stories.suggestion}:</p>
                <ul className="list-outside list-disc pl-4">
                  {translations.no_matching_stories.suggestions.map((t) => (
                    <li key={t}>{t}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}

      {!Builder.isEditing && props.enableInfiniteScroll && ctx.moreStoriesAvailable && <div ref={infiniteScrollRef} />}

      {!Builder.isEditing && ctx.loadingStories && (
        <div className="col-span-full flex items-center">
          <Spinner size={10} className="mx-auto my-16" />
        </div>
      )}
    </div>
  );
}

function StoryElement({
  story,
  selectGenre,
  maxTagsToShow,
}: {
  story: Story;
  selectGenre: (genre: string) => void;
  maxTagsToShow: number;
}) {
  return (
    <article className="group flex w-full flex-col items-start">
      <TranslatedLink href={buildStoryUrl(story)} className="relative w-full overflow-hidden">
        <img
          src={story.data.previewImage ?? PLACEHOLDER_IMAGE}
          alt={story.name}
          className="aspect-[4/5] w-full object-cover"
        />
      </TranslatedLink>

      {story.data.genres && story.data.genres.length > 0 ? (
        <div className="my-3.5 flex flex-row flex-wrap items-start gap-2">
          {story.data.genres.slice(0, maxTagsToShow).map((genre) => (
            <div
              key={genre}
              onClick={() => selectGenre(genre)}
              className={cn(
                "cursor-pointer",
                "bg-black text-xs font-medium uppercase text-white",
                "rounded-full px-2.5 py-0.5",
                "transition duration-300 hover:bg-gray-600",
              )}
            >
              {genre}
            </div>
          ))}
        </div>
      ) : (
        <div className="my-3.5 h-5">{/* Spacer */}</div>
      )}

      <TranslatedLink
        href={buildStoryUrl(story)}
        className="line-clamp-4 text-2xl transition-transform duration-200 group-hover:-skew-x-12"
      >
        {story.data.title}
      </TranslatedLink>
    </article>
  );
}

function SearchResults({ search, results }: { search: string; results: number }) {
  const {
    builder: { stories: translations },
  } = useTranslations();
  const resultCount = zeroPrefixedNumber(results);

  if (results === 0) return null;

  return (
    <div className={cn("flex justify-between gap-4", search === "" ? "sr-only:hidden opacity-0" : "opacity-100")}>
      <span className="break-all">{translations.search_results_for.replaceAll("$1", search)}</span>
      <span className="text-lightgray-300">({resultCount})</span>
    </div>
  );
}
